import React, { useState, useEffect } from 'react';
import { api, getProducts, downloadInvoicePDF } from '../services/api';

interface Product {
    id: number;
    name: string;
    price: number;
}

interface Stock {
  id: number;
  product_id: number;
  product_name: string;
  quantity: number;
  minimum_quantity: number;
}

interface SaleItem {
  product_id: number;
  quantity: number;
  unit_price: number;
  total_price: number;
}

interface SaleRecord {
  id: number;
  sale_date: string;
  total_amount: number;
  payment_method: string;
}

const Sales: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [saleItems, setSaleItems] = useState<SaleItem[]>([]);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [recentSales, setRecentSales] = useState<SaleRecord[]>([]);
  
  useEffect(() => {
    loadProducts();
    loadStocks();
    loadRecentSales();
  }, []);

  const loadProducts = async () => {
    const data = await getProducts();
    setProducts(data);
  };

  const loadStocks = async () => {
    try {
      const response = await api.get('/stocks/');
      setStocks(response.data);
    } catch (error) {
      console.error('Error loading stocks:', error);
    }
  };

  const loadRecentSales = async () => {
    try {
      const response = await api.get('/sales?limit=5');
      setRecentSales(response.data);
    } catch (error) {
      console.error('Error loading recent sales:', error);
      // Ne pas afficher d'erreur si les ventes récentes ne se chargent pas
      setRecentSales([]);
    }
  };

  const getStockForProduct = (productId: number): Stock | undefined => {
    return stocks.find(s => s.product_id === productId);
  };

  const handleAddItem = () => {
    setSaleItems([...saleItems, {
      product_id: 0,
      quantity: 1,
      unit_price: 0,
      total_price: 0
    }]);
  };

  const handleProductChange = (index: number, productId: number) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      const updated = [...saleItems];
      updated[index] = {
        ...updated[index],
        product_id: productId,
        unit_price: product.price,
        total_price: updated[index].quantity * product.price
      };
      setSaleItems(updated);
    }
  };

  const handleSubmitSale = async () => {
    try {
      // Vérifier que tous les items ont un produit sélectionné
      const invalidItems = saleItems.filter(item => item.product_id === 0);
      if (invalidItems.length > 0) {
        alert('Veuillez sélectionner un produit pour tous les articles');
        return;
      }

      // Vérifier le stock disponible
      for (const item of saleItems) {
        const stock = getStockForProduct(item.product_id);
        if (stock && stock.quantity < item.quantity) {
          const product = products.find(p => p.id === item.product_id);
          alert(`Stock insuffisant pour ${product?.name}. Disponible: ${stock.quantity}`);
          return;
        }
      }

      const sale = {
        sale_date: new Date().toISOString().split('T')[0],
        payment_method: paymentMethod,
        items: saleItems
      };
      
      await api.post('/sales/', sale);
      alert('Vente enregistrée avec succès!');
      setSaleItems([]);
      loadStocks(); // Recharger les stocks après la vente
      loadRecentSales(); // Recharger les ventes récentes
    } catch (error) {
      console.error('Error submitting sale:', error);
      alert('Erreur lors de l\'enregistrement de la vente');
    }
  };

  const updateItem = (index: number, field: keyof SaleItem, value: number) => {
    const updated = [...saleItems];
    updated[index] = { ...updated[index], [field]: value };
    
    if (field === 'quantity' || field === 'unit_price') {
      updated[index].total_price = updated[index].quantity * updated[index].unit_price;
    }
    
    setSaleItems(updated);
  };

  const removeItem = (index: number) => {
    setSaleItems(saleItems.filter((_, i) => i !== index));
  };

  const handleDownloadInvoice = async (saleId: number) => {
    try {
      await downloadInvoicePDF(saleId);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Erreur lors du téléchargement de la facture');
    }
  };

  const totalAmount = saleItems.reduce((sum, item) => sum + item.total_price, 0);

  return (
    <div className="sales-container">
      <div className="page-header">
        <h2>💰 Enregistrement des Ventes</h2>
      </div>
      
      <div className="sale-form">
        <div className="form-group">
          <label htmlFor="payment-method">Mode de paiement:</label>
          <select 
            id="payment-method"
            value={paymentMethod} 
            onChange={(e) => setPaymentMethod(e.target.value)}
          >
            <option value="cash">💵 Espèces</option>
            <option value="card">💳 Carte</option>
            <option value="check">📝 Chèque</option>
            <option value="transfer">🏦 Virement</option>
          </select>
        </div>

        <div className="sale-items-list">
          <h3>Articles vendus</h3>
          {saleItems.map((item, index) => {
            const stock = getStockForProduct(item.product_id);
            const itemKey = item.product_id > 0 ? `item-${item.product_id}-${index}` : `new-item-${index}`;
            
            return (
              <div key={itemKey} className="sale-item-row">
                <div className="sale-item-fields">
                  <div className="form-group">
                    <label>Produit</label>
                    <select
                      value={item.product_id}
                      onChange={(e) => handleProductChange(index, Number.parseInt(e.target.value))}
                      className="product-select"
                    >
                      <option value="0">Sélectionner un produit</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} - {p.price.toFixed(2)} CFA
                        </option>
                      ))}
                    </select>
                    {stock && (
                      <div className="stock-info">
                        Stock: {stock.quantity} 
                        {stock.quantity <= stock.minimum_quantity && (
                          <span className="stock-warning"> ⚠️</span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>Quantité</label>
                    <input
                      type="number"
                      placeholder="Quantité"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', Number.parseFloat(e.target.value))}
                      inputMode="decimal"
                      min="0.01"
                      step="0.01"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Prix unitaire</label>
                    <input
                      type="number"
                      placeholder="Prix unitaire"
                      value={item.unit_price}
                      onChange={(e) => updateItem(index, 'unit_price', Number.parseFloat(e.target.value))}
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Total</label>
                    <div className="total-display">{item.total_price.toFixed(2)} CFA</div>
                  </div>
                </div>
                
                <button 
                  onClick={() => removeItem(index)} 
                  className="btn-icon btn-delete"
                  aria-label="Supprimer l'article"
                  type="button"
                >
                  🗑️ Retirer
                </button>
              </div>
            );
          })}
          
          <button onClick={handleAddItem} className="btn-primary" type="button">
            ➕ Ajouter un article
          </button>
        </div>

        <div className="summary-section">
          <div className="summary-row">
            <span>Nombre d'articles:</span>
            <strong>{saleItems.length}</strong>
          </div>
          <div className="summary-row">
            <span>Total à payer:</span>
            <strong>{totalAmount.toFixed(2)} CFA</strong>
          </div>
        </div>

        <button 
          onClick={handleSubmitSale} 
          className="btn-submit btn-primary"
          disabled={saleItems.length === 0}
        >
          ✅ Enregistrer la vente
        </button>
      </div>

      {recentSales.length > 0 && (
        <div className="recent-sales">
          <h3>📊 Ventes récentes</h3>
          
          {/* Desktop: Table View */}
          <div className="sales-table-view">
            <div className="sales-list">
              {recentSales.map(sale => (
                <div key={sale.id} className="sale-record">
                  <div className="sale-info">
                    <span className="sale-id">#{sale.id}</span>
                    <span className="sale-date">📅 {new Date(sale.sale_date).toLocaleDateString('fr-FR')}</span>
                    <span className="sale-amount">{sale.total_amount.toFixed(2)} CFA</span>
                    <span className="sale-payment">{sale.payment_method === 'cash' ? '💵 Espèces' : '💳 Carte'}</span>
                  </div>
                  <button
                    onClick={() => handleDownloadInvoice(sale.id)}
                    className="btn-download"
                    type="button"
                  >
                    📥 Télécharger
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile: Cards View */}
          <div className="sales-cards-view">
            {recentSales.map(sale => (
              <div key={sale.id} className="sale-card">
                <div className="sale-card-header">
                  <span className="sale-id-badge">#{sale.id}</span>
                  <span className="sale-date">📅 {new Date(sale.sale_date).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="sale-card-body">
                  <div className="sale-info-grid">
                    <div className="sale-info-item">
                      <span className="info-label">Montant</span>
                      <span className="info-value price-value">{sale.total_amount.toFixed(2)} CFA</span>
                    </div>
                    <div className="sale-info-item">
                      <span className="info-label">Paiement</span>
                      <span className="info-value">{sale.payment_method === 'cash' ? '💵 Espèces' : '💳 Carte'}</span>
                    </div>
                  </div>
                </div>
                <div className="sale-card-actions">
                  <button
                    onClick={() => handleDownloadInvoice(sale.id)}
                    className="btn-card btn-download"
                    type="button"
                  >
                    <span className="btn-icon">📥</span>
                    <span className="btn-text">Télécharger Facture</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Sales;